/**
 * Pure algorithm engine for iterative constrained kernel PCA.
 *
 * Faithful port of ConstrainedKPCAIterative from Embedder.py.
 * All functions operate on typed arrays for performance.
 * No React or DOM dependencies.
 */

import { InVisData } from "./invis-types";

// --- Algorithm parameters (matching original Python) ---
const CP_CONST_MU = 50;
const ORTH_MU = 10;
const INITIAL_LR = 0.1;
const BETA = 0.95;
const CONVERGENCE_TOL = 1e-9;
const MAX_FRAME_MS = 16; // time budget per solve call (~60fps)

// --- Engine state (mutable, managed by the hook) ---
export interface EngineState {
  v1: Float64Array;
  v2: Float64Array;
  embedding: Float64Array; // interleaved [x0, y0, x1, y1, ...]
  learningRate: number;    // shared lr, matching original
  // Cached C_cp - only recomputed when control point SET changes (not on move)
  cachedCcp: number[][] | null;
  cachedCpIndices: Set<number>;
}

// --- Initialization ---

export function createInitialState(data: InVisData): EngineState {
  const n = data.n;

  const v1 = new Float64Array(data.initialV1);
  const v2 = new Float64Array(data.initialV2);
  const embedding = new Float64Array(n * 2);

  for (let i = 0; i < n; i++) {
    embedding[i * 2] = data.initialEmbedding[i][0];
    embedding[i * 2 + 1] = data.initialEmbedding[i][1];
  }

  return {
    v1,
    v2,
    embedding,
    learningRate: INITIAL_LR,
    cachedCcp: null,
    cachedCpIndices: new Set(),
  };
}

export function resetState(state: EngineState, data: InVisData): void {
  const nL = data.nLandmarks;
  const n = data.n;

  for (let j = 0; j < nL; j++) {
    state.v1[j] = data.initialV1[j];
    state.v2[j] = data.initialV2[j];
  }
  state.learningRate = INITIAL_LR;
  state.cachedCcp = null;
  state.cachedCpIndices = new Set();

  for (let i = 0; i < n; i++) {
    state.embedding[i * 2] = data.initialEmbedding[i][0];
    state.embedding[i * 2 + 1] = data.initialEmbedding[i][1];
  }
}

// --- Linear algebra helpers ---

function vecNorm(a: Float64Array): number {
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += a[i] * a[i];
  return Math.sqrt(sum);
}

/**
 * Compute out = M @ v where M is stored as number[][] (row-major)
 */
function matvec(M: number[][], v: Float64Array, out: Float64Array): void {
  const rows = M.length;
  for (let i = 0; i < rows; i++) {
    let sum = 0;
    const row = M[i];
    for (let j = 0; j < v.length; j++) sum += row[j] * v[j];
    out[i] = sum;
  }
}

function vecDiffNorm(a: Float64Array, b: Float64Array): number {
  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i];
    sum += d * d;
  }
  return Math.sqrt(sum);
}

// --- Core: Build C_cp matrix (only when control point set changes) ---

function buildCcp(
  data: InVisData,
  controlPointIndices: number[],
): number[][] {
  const nL = data.nLandmarks;
  const m = controlPointIndices.length;
  if (m === 0) return [];

  const weight = CP_CONST_MU / m;
  const Ccp: number[][] = new Array(nL);
  for (let i = 0; i < nL; i++) {
    Ccp[i] = new Array(nL).fill(0);
  }

  for (const idx of controlPointIndices) {
    const Si = data.S[idx];
    for (let a = 0; a < nL; a++) {
      const wSa = weight * Si[a];
      for (let b = a; b < nL; b++) {
        const val = wSa * Si[b];
        Ccp[a][b] += val;
        if (b !== a) Ccp[b][a] += val;
      }
    }
  }

  return Ccp;
}

// --- Core: Solve iteratively for one eigenvector ---
// Faithful port of ConstrainedKPCAIterative.solve_iteratively

function solveIteratively(
  C: number[][],       // nL x nL: combined matrix (C_var - C_cp [- orth_mu * outer(prev_v)])
  b: Float64Array,     // nL: target bias vector (already negative)
  v: Float64Array,     // nL: initial v (mutated in place)
  learningRate: { value: number },
  nL: number,
  timeBudgetMs: number,
): void {
  const startTime = performance.now();

  const v_dw = new Float64Array(nL); // velocity, reset each call (matching original)
  const grad = new Float64Array(nL);
  const v_lookahead = new Float64Array(nL);
  const v_new = new Float64Array(nL);
  const beta = BETA;
  const lr = learningRate;

  while (true) {
    // Time check (matching original's frame-rate budget)
    const elapsed = performance.now() - startTime;
    if (elapsed > timeBudgetMs) break;

    // Nesterov lookahead: v_look = v + beta * v_dw
    for (let j = 0; j < nL; j++) {
      v_lookahead[j] = v[j] + beta * v_dw[j];
    }

    // Gradient: dw = C @ v_lookahead - b.T
    // (b already has negative sign baked in, so - b.T = - (-...) = + target term)
    matvec(C, v_lookahead, grad);
    for (let j = 0; j < nL; j++) {
      grad[j] -= b[j];
    }

    // Velocity update: v_dw = beta * v_dw + lr * dw
    for (let j = 0; j < nL; j++) {
      v_dw[j] = beta * v_dw[j] + lr.value * grad[j];
    }

    // Update: v_new = v + v_dw
    for (let j = 0; j < nL; j++) {
      v_new[j] = v[j] + v_dw[j];
    }

    // adjust_learning_rate (matching original exactly)
    let continueFlag = false;

    // Check 1: norm(v_new - v) > 1.5 BEFORE normalizing
    if (vecDiffNorm(v_new, v) > 1.5) {
      lr.value = lr.value / 2;
      continueFlag = true;
    }

    // Normalize v_new
    const n = vecNorm(v_new);
    if (n > 1e-15) {
      const inv = 1 / n;
      for (let j = 0; j < nL; j++) v_new[j] *= inv;
    }

    // Check 2: norm(v_new_normalized - v) > 1.5 AFTER normalizing
    if (vecDiffNorm(v_new, v) > 1.5) {
      lr.value = lr.value / 2;
      continueFlag = true;
    }

    // Check 3: v_new collapsed to zero
    if (n < 1e-8) {
      lr.value = lr.value / 2;
      continueFlag = true;
    }

    if (continueFlag) {
      // Reset velocity and retry
      v_dw.fill(0);
      continue;
    }

    // Convergence check
    if (vecDiffNorm(v_new, v) < CONVERGENCE_TOL) {
      break;
    }

    // Accept update: v = v_new
    for (let j = 0; j < nL; j++) v[j] = v_new[j];
  }
}

/**
 * Run a full optimization update for the current control point configuration.
 * This matches the original's _benchmark_iteration + _benchmark_update_cp_params flow.
 *
 * The original runs a full convergence loop for v1, then for v2.
 */
export function runOptimization(
  data: InVisData,
  state: EngineState,
  controlPoints: Map<number, { targetX: number; targetY: number }>,
): void {
  const nL = data.nLandmarks;
  const m = controlPoints.size;

  if (m === 0) {
    return;
  }

  // Check if control point SET changed -> rebuild C_cp
  const currentIndices: number[] = [];
  controlPoints.forEach((_, idx) => currentIndices.push(idx));
  const currentSet = new Set(currentIndices);
  const setChanged = currentSet.size !== state.cachedCpIndices.size ||
    currentIndices.some(idx => !state.cachedCpIndices.has(idx));

  if (setChanged || !state.cachedCcp) {
    state.cachedCcp = buildCcp(data, currentIndices);
    state.cachedCpIndices = currentSet;
  }

  const Ccp = state.cachedCcp;

  // Build C = C_var - C_cp
  const C: number[][] = new Array(nL);
  for (let i = 0; i < nL; i++) {
    C[i] = new Array(nL);
    for (let j = 0; j < nL; j++) {
      C[i][j] = data.C_var[i][j] - Ccp[i][j];
    }
  }

  // Build b for dimension 0 (targetX) and dimension 1 (targetY)
  // b = (-cp_const_mu / m) * sum_k( Y_k * S[cp_k] )
  const b0 = new Float64Array(nL);
  const b1 = new Float64Array(nL);
  const weight = -CP_CONST_MU / m;

  controlPoints.forEach(({ targetX, targetY }, idx) => {
    const Si = data.S[idx];
    for (let j = 0; j < nL; j++) {
      b0[j] += weight * targetX * Si[j];
      b1[j] += weight * targetY * Si[j];
    }
  });

  // Shared learning rate reference (matching original: single self.learning_rate)
  const lr = { value: state.learningRate };

  // Solve v1 (half the frame budget)
  solveIteratively(C, b0, state.v1, lr, nL, MAX_FRAME_MS / 2);

  // Build C2 = C - orth_mu * outer(v1, v1) for v2 orthogonality
  const C2: number[][] = new Array(nL);
  for (let i = 0; i < nL; i++) {
    C2[i] = new Array(nL);
    for (let j = 0; j < nL; j++) {
      C2[i][j] = C[i][j] - ORTH_MU * state.v1[i] * state.v1[j];
    }
  }

  // Solve v2 (other half of frame budget)
  solveIteratively(C2, b1, state.v2, lr, nL, MAX_FRAME_MS / 2);

  // Save learning rate back
  state.learningRate = lr.value;

  // Recompute embedding
  recomputeEmbedding(data, state);
}

/**
 * Recompute the 2D embedding from current v1, v2.
 * embedding = [v1, v2]^T @ S^T  (matching original's projection_matrix @ S.T)
 */
export function recomputeEmbedding(data: InVisData, state: EngineState): void {
  const n = data.n;
  const nL = data.nLandmarks;
  const { v1, v2, embedding } = state;

  for (let i = 0; i < n; i++) {
    const Si = data.S[i];
    let x = 0, y = 0;
    for (let j = 0; j < nL; j++) {
      x += Si[j] * v1[j];
      y += Si[j] * v2[j];
    }
    embedding[i * 2] = x;
    embedding[i * 2 + 1] = y;
  }
}

/**
 * Notify the engine that control points changed (new point added or removed).
 * Resets learning rate for fresh optimization.
 */
export function onControlPointsChanged(state: EngineState): void {
  state.learningRate = INITIAL_LR;
}
